import type {HistoryEntitySnapshot} from '../../../history';
import {createExistingHistorySnapshot, createMissingHistorySnapshot} from '../../../history';
import {isSerializablePlainRecord} from '../../../history/domain/SerializablePlainData';
import type {SketchEntityReferenceRecord} from '../model/SketchEntityReferenceRecord';
import type {
	SketchEntityReferenceHistorySnapshot,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceHistorySnapshot';

export type {
	SketchEntityReferenceHistorySnapshot,
} from '../../../../types/reference/sketch-entity-reference/SketchEntityReferenceHistorySnapshot';

/**
 * 深拷贝引用记录。
 *
 * @param reference 引用记录。
 * @returns 引用记录副本。
 */
function cloneReference(reference: SketchEntityReferenceRecord): SketchEntityReferenceRecord {
	return JSON.parse(JSON.stringify(reference)) as SketchEntityReferenceRecord;
}

/**
 * 创建草图实体引用历史快照。
 *
 * @param reference 引用记录。
 * @returns 可序列化历史快照。
 */
export function createSketchEntityReferenceHistorySnapshot(
	reference: SketchEntityReferenceRecord,
): SketchEntityReferenceHistorySnapshot {
	return {
		referenceId: reference.referenceId,
		reference: cloneReference(reference),
	};
}

/**
 * 判断草图实体引用快照是否为可序列化 plain data。
 *
 * @param snapshot 草图实体引用历史快照。
 * @returns 快照满足 plain data 契约时返回 true。
 */
export function isPlainSketchEntityReferenceHistorySnapshot(
	snapshot: SketchEntityReferenceHistorySnapshot,
): boolean {
	return typeof snapshot.referenceId === 'string'
		&& isSerializablePlainRecord(snapshot.reference)
		&& snapshot.reference.referenceId === snapshot.referenceId;
}

/**
 * 从仓储读取结果创建引用历史实体快照。
 *
 * @param referenceId 引用记录 ID。
 * @param references 仓储读取到的引用记录列表。
 * @returns 存在或缺失的历史实体快照。
 */
export function createSketchEntityReferenceHistorySnapshotFromRepositoryResult(
	referenceId: string,
	references: readonly SketchEntityReferenceRecord[],
): HistoryEntitySnapshot<SketchEntityReferenceHistorySnapshot> {
	const reference = references.find((candidate) => candidate.referenceId === referenceId);

	if (!reference) {
		return createMissingHistorySnapshot();
	}

	return createExistingHistorySnapshot(createSketchEntityReferenceHistorySnapshot(reference));
}
